import React, { useMemo } from 'react';
import { Redirect, useHistory } from 'react-router-dom';

import i18n from 'i18n-js';

import {
    formValuesToModel,
    getDatePart,
    getNewReservationFormValues,
    getRestaurantWallClock,
    ReservationFormValues,
    validateNewReservationForm,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import ReservationEditor from './components/ReservationEditor';
import { FormSkeleton } from 'components/shared';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { RESERVATIONS_PATH, useReservationsSubpageGuard } from './hooks';
import { ACTIVE_TAB_STATE } from './index';

export default function NewReservationPage() {
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const isModuleDisabled = useReservationsSubpageGuard();
    const settings = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.reservationModule
    );
    const timeZone = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.timeZone
    );
    const isInformationSettled = useAppSelector(
        (state) => state.restaurantDetails.isRestaurantInformationSettled
    );
    const initialValues = useMemo(
        () => settings && getNewReservationFormValues(settings, getRestaurantWallClock(timeZone)),
        [settings, timeZone]
    );

    const backToActive = () => history.replace(RESERVATIONS_PATH, ACTIVE_TAB_STATE);

    const handleCreate = async (values: ReservationFormValues): Promise<string | null> => {
        try {
            const result = await dispatch(
                reservationsStore.actionCreators.createReservation(formValuesToModel(values))
            );
            if (result.status === 'tableTaken') {
                return result.tableId;
            }
            const { email, channel, reservationStartDateTime } = result.reservation;
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    email && channel !== 'WalkIn'
                        ? i18n.t('reservations.confirmedEmailSent', { email })
                        : i18n.t('reservations.confirmedNoEmail')
                )
            );
            history.replace(RESERVATIONS_PATH, {
                ...ACTIVE_TAB_STATE,
                date: getDatePart(reservationStartDateTime),
            });
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        }
        return null;
    };

    if (isModuleDisabled) {
        return <Redirect to={RESERVATIONS_PATH} />;
    }

    return (
        <div style={{ position: 'relative', height: '100%' }}>
            {initialValues && (
                <ReservationEditor
                    title={i18n.t('reservations.newReservation').toUpperCase()}
                    channelLine={null}
                    reservationId={null}
                    initialValues={initialValues}
                    validate={validateNewReservationForm}
                    confirmLabel={i18n.t('reservations.createReservation')}
                    onCancel={backToActive}
                    onConfirm={handleCreate}
                />
            )}
            <FormSkeleton
                isLoading={!initialValues && !isInformationSettled}
                title
                columns={[]}
                aside={6}
                actions={2}
            />
        </div>
    );
}
